import {
  type ConsentStatus,
  readConsentCookie,
  writeConsentCookie,
} from "./consent";

export type GtagConsentState = "granted" | "denied";

export type AnalyticsEventParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let measurementId: string | null = null;
let initialized = false;

export function getMeasurementId(): string | null {
  const id = import.meta.env.VITE_GA_MEASUREMENT_ID;
  return typeof id === "string" && id.length > 0 ? id : null;
}

export function ensureGtagQueue(): void {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
  }
}

export function buildConsentDefault(status: ConsentStatus | null): {
  analytics_storage: GtagConsentState;
  ad_storage: GtagConsentState;
  ad_user_data: GtagConsentState;
  ad_personalization: GtagConsentState;
} {
  const analytics = status === "granted" ? "granted" : "denied";

  return {
    analytics_storage: analytics,
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  };
}

function loadGtagScript(id: string): void {
  if (document.querySelector(`script[data-ga-measurement-id="${id}"]`)) {
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  script.dataset.gaMeasurementId = id;
  document.head.appendChild(script);
}

export function initAnalytics(): boolean {
  if (initialized) {
    return measurementId !== null;
  }

  initialized = true;
  measurementId = getMeasurementId();

  if (!measurementId || typeof window === "undefined") {
    return false;
  }

  ensureGtagQueue();

  const consent = readConsentCookie();
  window.gtag!("consent", "default", buildConsentDefault(consent));
  window.gtag!("js", new Date());
  window.gtag!("config", measurementId, {
    send_page_view: false,
    cookie_domain: "auto",
    debug_mode: !import.meta.env.PROD,
  });

  loadGtagScript(measurementId);
  return true;
}

export function applyConsentUpdate(status: ConsentStatus): void {
  writeConsentCookie(status);
  ensureGtagQueue();
  window.gtag?.("consent", "update", buildConsentDefault(status));
}

export function trackEvent(eventName: string, params?: AnalyticsEventParams): void {
  if (!measurementId || typeof window.gtag !== "function") {
    return;
  }

  window.gtag("event", eventName, params);
}

export function trackPageView(path: string, title?: string): void {
  if (!measurementId || typeof window.gtag !== "function") {
    return;
  }

  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
}

export function isAnalyticsEnabled(): boolean {
  return getMeasurementId() !== null;
}

export function getInitialConsentStatus(): ConsentStatus | null {
  return readConsentCookie();
}
